import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { 
  Globe, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle, 
  Clock,
  RefreshCw,
  Play,
  Undo,
  Settings as SettingsIcon
} from 'lucide-react';

interface SEOAudit {
  id: string;
  brand_key: string;
  page_url: string;
  overall_score: number;
  technical_score: number;
  on_page_score: number;
  content_score: number;
  performance_score: number;
  audit_date: string;
  issues_found: any[];
}

interface SEOOptimization {
  id: string;
  brand_key: string;
  post_id: string;
  page_url: string;
  optimization_type: string;
  confidence_score: number;
  status: 'pending' | 'applied' | 'failed' | 'reverted';
  expected_impact: string;
  reasoning: string;
  created_at: string;
  applied_at?: string;
}

interface WordPressSiteConfig {
  brand_key: string;
  site_url: string;
  auto_optimize_enabled: boolean;
  auto_optimize_threshold: number;
}

export default function WordPressSEOPage() {
  const [audits, setAudits] = useState<SEOAudit[]>([]);
  const [optimizations, setOptimizations] = useState<SEOOptimization[]>([]);
  const [siteConfigs, setSiteConfigs] = useState<WordPressSiteConfig[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'audits' | 'optimizations' | 'settings'>('audits');

  useEffect(() => {
    loadData();
  }, [selectedBrand]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load site configs
      const { data: configs } = await supabase
        .from('wordpress_site_config')
        .select('brand_key, site_url, auto_optimize_enabled, auto_optimize_threshold')
        .order('brand_key');
      
      if (configs) setSiteConfigs(configs);

      // Load audits
      let auditsQuery = supabase
        .from('wordpress_seo_audits')
        .select('*')
        .order('audit_date', { ascending: false })
        .limit(50);
      
      if (selectedBrand !== 'all') {
        auditsQuery = auditsQuery.eq('brand_key', selectedBrand);
      }

      const { data: auditsData } = await auditsQuery;
      if (auditsData) setAudits(auditsData);

      // Load optimizations
      let optsQuery = supabase
        .from('wordpress_seo_optimizations')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (selectedBrand !== 'all') {
        optsQuery = optsQuery.eq('brand_key', selectedBrand);
      }

      const { data: optsData } = await optsQuery;
      if (optsData) setOptimizations(optsData);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAutoOptimization = async (brandKey: string, enabled: boolean) => {
    try {
      await supabase
        .from('wordpress_site_config')
        .update({ auto_optimize_enabled: enabled })
        .eq('brand_key', brandKey);
      
      loadData();
    } catch (error) {
      console.error('Error updating config:', error);
    }
  };

  const updateThreshold = async (brandKey: string, threshold: number) => {
    try {
      await supabase
        .from('wordpress_site_config')
        .update({ auto_optimize_threshold: threshold })
        .eq('brand_key', brandKey);
      
      loadData();
    } catch (error) {
      console.error('Error updating threshold:', error);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'applied':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      case 'reverted':
        return <Undo className="h-5 w-5 text-gray-600" />;
      default:
        return null;
    }
  };

  const stats = {
    totalAudits: audits.length,
    avgScore: audits.length > 0 
      ? Math.round(audits.reduce((sum, a) => sum + a.overall_score, 0) / audits.length)
      : 0,
    pendingOptimizations: optimizations.filter(o => o.status === 'pending').length,
    appliedOptimizations: optimizations.filter(o => o.status === 'applied').length
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Globe className="h-8 w-8 text-blue-600" />
          WordPress SEO Management
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          AI-powered SEO optimization with GPT-4 integration
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Audits</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalAudits}</p>
            </div>
            <Globe className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Average Score</p>
              <p className={`text-2xl font-bold ${stats.avgScore >= 80 ? 'text-green-600' : stats.avgScore >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                {stats.avgScore}
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pendingOptimizations}</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Applied</p>
              <p className="text-2xl font-bold text-green-600">{stats.appliedOptimizations}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>
      </div>

      {/* Brand Filter */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Brand:</label>
            <select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="all">All Brands</option>
              <option value="hales_ac">Hale's AC</option>
              <option value="climate_control_services">Climate Control Services</option>
              <option value="abraham_ac">Abraham AC</option>
            </select>
          </div>

          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('audits')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'audits'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              SEO Audits ({audits.length})
            </button>
            <button
              onClick={() => setActiveTab('optimizations')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'optimizations'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Optimizations ({optimizations.length})
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <SettingsIcon className="h-4 w-4 inline mr-1" />
              Settings
            </button>
          </nav>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
              <p className="mt-2 text-gray-600">Loading data...</p>
            </div>
          ) : activeTab === 'audits' ? (
            <div className="space-y-4">
              {audits.length === 0 ? (
                <p className="text-center text-gray-500 py-12">No audits found</p>
              ) : (
                audits.map((audit) => (
                  <div key={audit.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{audit.page_url}</h3>
                        <p className="text-sm text-gray-600 capitalize">{audit.brand_key.replace('_', ' ')}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(audit.audit_date).toLocaleString()}
                        </p>
                      </div>
                      <div className={`px-3 py-1 rounded-full font-semibold ${getScoreColor(audit.overall_score)}`}>
                        {audit.overall_score}
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-4 mt-4">
                      <div>
                        <p className="text-xs text-gray-600">Technical</p>
                        <p className={`text-sm font-semibold ${getScoreColor(audit.technical_score)}`}>
                          {audit.technical_score}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">On-Page</p>
                        <p className={`text-sm font-semibold ${getScoreColor(audit.on_page_score)}`}>
                          {audit.on_page_score}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Content</p>
                        <p className={`text-sm font-semibold ${getScoreColor(audit.content_score)}`}>
                          {audit.content_score}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Performance</p>
                        <p className={`text-sm font-semibold ${getScoreColor(audit.performance_score)}`}>
                          {audit.performance_score}
                        </p>
                      </div>
                    </div>

                    {audit.issues_found && audit.issues_found.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs font-medium text-gray-700 mb-1">Issues Found:</p>
                        <div className="flex flex-wrap gap-2">
                          {audit.issues_found.slice(0, 3).map((issue, idx) => (
                            <span key={idx} className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                              {issue}
                            </span>
                          ))}
                          {audit.issues_found.length > 3 && (
                            <span className="text-xs text-gray-500">+{audit.issues_found.length - 3} more</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          ) : activeTab === 'optimizations' ? (
            <div className="space-y-4">
              {optimizations.length === 0 ? (
                <p className="text-center text-gray-500 py-12">No optimizations found</p>
              ) : (
                optimizations.map((opt) => (
                  <div key={opt.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        {getStatusIcon(opt.status)}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-gray-900">{opt.page_url}</h3>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              {opt.optimization_type}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{opt.reasoning}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span className="capitalize">{opt.brand_key.replace('_', ' ')}</span>
                            <span>•</span>
                            <span>Confidence: {opt.confidence_score}%</span>
                            <span>•</span>
                            <span className="capitalize">Impact: {opt.expected_impact}</span>
                            <span>•</span>
                            <span>{new Date(opt.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                        opt.status === 'applied' ? 'bg-green-100 text-green-700' :
                        opt.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        opt.status === 'failed' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {opt.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {siteConfigs.map((config) => (
                <div key={config.brand_key} className="border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 capitalize mb-4">
                    {config.brand_key.replace('_', ' ')}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">{config.site_url}</p>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-gray-700">
                          Auto-Optimization
                        </label>
                        <p className="text-xs text-gray-500">
                          Automatically apply high-confidence optimizations
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={config.auto_optimize_enabled}
                          onChange={(e) => toggleAutoOptimization(config.brand_key, e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-2">
                        Auto-Approval Threshold: {config.auto_optimize_threshold}%
                      </label>
                      <input
                        type="range"
                        min="70"
                        max="95"
                        step="5"
                        value={config.auto_optimize_threshold}
                        onChange={(e) => updateThreshold(config.brand_key, parseInt(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>70%</span>
                        <span>95%</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Optimizations with confidence ≥ {config.auto_optimize_threshold}% will be auto-applied
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">GPT-4 Integration</h4>
                <p className="text-sm text-blue-700">
                  Phase 2 enhancements use GPT-4 to generate AI-powered meta titles and descriptions,
                  with intelligence from DataForSEO keywords, rank tracking, and Web Vitals.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
